/**
 * HashtagPlugin.tsx
 *
 * Lexical plugin that provides HashtagNode for future use.
 *
 * CRITICAL FIX (2025-12-05):
 * - Removed node transformation that caused cursor jumping bug
 * - Hashtag styling is now handled by PostEditor via CSS (see PostEditor.tsx)
 * - This plugin only registers the HashtagNode type for potential future use
 *
 * Previous bug: When user typed "this is a #test " (with space), cursor would
 * jump backwards to before the hashtag, causing "this is a hashtag #test" instead
 * of the correct "this is a #test hashtag".
 *
 * Root cause: textNodeTransform split nodes and attempted to restore cursor position,
 * but the selection object became stale after node.replace(), causing incorrect placement.
 *
 * Solution: Don't transform nodes at all. Use CSS-only styling in parent component.
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  TextNode,
  $createTextNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
} from 'lexical';
import { useEffect } from 'react';

/**
 * Custom Lexical node for hashtags
 * NOTE: Currently NOT used in transformation (to avoid cursor bugs)
 * Kept for potential future use in serialization/export features
 */
export class HashtagNode extends TextNode {
  static getType(): string {
    return 'hashtag';
  }

  static clone(node: HashtagNode): HashtagNode {
    return new HashtagNode(node.__text, node.__key);
  }

  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.className = 'bg-secondary-500 text-secondary-500-contrast px-1 rounded font-medium';
    element.style.fontWeight = '500';
    return element;
  }

  updateDOM(
    prevNode: HashtagNode,
    dom: HTMLElement,
    _config: EditorConfig
  ): boolean {
    const textChanged = prevNode.__text !== this.__text;
    if (textChanged) {
      dom.textContent = this.__text;
    }
    dom.className = 'bg-secondary-500 text-secondary-500-contrast px-1 rounded font-medium';
    return textChanged;
  }

  static importJSON(serializedNode: SerializedTextNode): TextNode {
    const node = $createHashtagNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): SerializedTextNode {
    return {
      ...super.exportJSON(),
      type: 'hashtag',
      version: 1,
    };
  }

  canInsertTextBefore(): boolean {
    return true;
  }

  canInsertTextAfter(): boolean {
    return true;
  }

  isTextEntity(): boolean {
    return true;
  }
}

/**
 * Helper function to create a HashtagNode
 */
export function $createHashtagNode(text: string): HashtagNode {
  return new HashtagNode(text);
}

/**
 * Type guard to check if a node is a HashtagNode
 */
export function $isHashtagNode(
  node: LexicalNode | null | undefined
): node is HashtagNode {
  return node instanceof HashtagNode;
}

/**
 * HashtagPlugin component
 *
 * SIMPLIFIED: This plugin now ONLY registers the HashtagNode type.
 * It does NOT perform any transformations or DOM modifications.
 *
 * Hashtag styling is handled via CSS in the parent PostEditor component.
 * This ensures no cursor manipulation and no cursor jumping bugs.
 */
export function HashtagPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Register the node type
    if (!editor.hasNodes([HashtagNode])) {
      throw new Error('HashtagPlugin: HashtagNode not registered on editor');
    }

    // Regex to match hashtags — must start with letter after # to match HashtagNode validation
    // Unicode-aware; a token only counts if it contains at least one letter (excludes #123, #_)
    const HASHTAG_REGEX = /(^|\s)(#[\p{L}\p{N}\p{M}_]+)/gu;
    const HAS_LETTER = /\p{L}/u;

    // Transform TextNode to HashtagNode
    const removeTransform = editor.registerNodeTransform(TextNode, (textNode: TextNode) => {
      // Skip if already a HashtagNode
      if ($isHashtagNode(textNode)) {
        return;
      }

      const textContent = textNode.getTextContent();
      // First candidate that actually contains a letter
      let match: RegExpMatchArray | undefined;
      for (const candidate of textContent.matchAll(HASHTAG_REGEX)) {
        if (HAS_LETTER.test(candidate[2])) {
          match = candidate;
          break;
        }
      }

      if (match && match.index !== undefined) {
        const hashtagText = match[2]; // The #hashtag part
        const startOffset = match.index + match[1].length; // Skip leading space
        const endOffset = startOffset + hashtagText.length;

        let targetNode: TextNode;

        // Split the text node to isolate the hashtag
        if (startOffset === 0) {
          // Hashtag is at the start
          [targetNode] = textNode.splitText(endOffset);
        } else {
          // Hashtag is in the middle or end
          [, targetNode] = textNode.splitText(startOffset);
          [targetNode] = targetNode.splitText(hashtagText.length);
        }

        // Replace with HashtagNode
        const hashtagNode = $createHashtagNode(hashtagText);
        targetNode.replace(hashtagNode);

        // DO NOT manually set selection - let Lexical handle it naturally
        // This was the cause of the cursor jumping bug
      }
    });

    // Transform HashtagNode when invalid text is added
    const removeHashtagTransform = editor.registerNodeTransform(HashtagNode, (hashtagNode: HashtagNode) => {
      const text = hashtagNode.getTextContent();

      // Valid hashtag pattern: #<letter><alphanumeric_underscore>*
      // If text has a space or doesn't match pattern, we need to split
      const validHashtagMatch = /^#[\p{L}\p{N}\p{M}_]+/u.exec(text);

      if (!validHashtagMatch || !HAS_LETTER.test(validHashtagMatch[0])) {
        // Entire thing is invalid, convert to TextNode
        const textNode = $createTextNode(text);
        hashtagNode.replace(textNode);
        return;
      }

      const validHashtag = validHashtagMatch[0];

      if (validHashtag.length < text.length) {
        // There's extra text after the hashtag - need to split
        const remainingText = text.slice(validHashtag.length);

        // Update hashtag node to only contain valid part
        hashtagNode.setTextContent(validHashtag);

        // Create a new text node for the remaining text and insert after
        const textNode = $createTextNode(remainingText);
        hashtagNode.insertAfter(textNode);

        // Move selection to end of the new text node (where user was typing)
        textNode.selectEnd();
      }
    });

    // Cleanup
    return () => {
      removeTransform();
      removeHashtagTransform();
    };
  }, [editor]);

  return null;
}

export default HashtagPlugin;
